import { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { Avatar } from '../../../components/Avatar'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { ReportButton } from '../../../components/ReportButton'
import { useAuth } from '../../../lib/auth'
import {
  dishDisplayName, fetchGallery, fetchMyVotedSubmissionIds,
  fetchSubmissionAuthors, toggleWeeklyVote, weekLabel,
} from '../../../lib/api/weekly'
import { useMutedIds, MUTED_PLACEHOLDER } from '../../../lib/api/mutes'
import { supabase } from '../../../lib/supabase'
import { readError } from '../../../lib/errors'
import type {
  WeeklyEventRow, WeeklySubmissionRow,
} from '../../../lib/database.types'
import { useT } from '../../../i18n'

interface Props {
  eventId: string
  onBack: () => void
  /** Open the author's public profile when their name is tapped. */
  onOpenProfile?: (profileId: string) => void
}

/** Mirror of the DB-enforced cap. Keep in sync with cast_weekly_vote. */
const MAX_VOTES_PER_VOTER = 2

interface AuthorInfo { display_name: string; avatar_url: string | null }

/**
 * View + vote on a past week's submissions. Voting is a toggle — tap
 * "I'd eat that" to register, tap again to un-vote. Each submission's
 * total_votes is re-read from the RPC response so the UI stays in sync.
 */
export function VotingScreen({ eventId, onBack, onOpenProfile }: Props) {
  const t = useT()
  const { profile, session, exitGuestMode } = useAuth()
  const { mutedIds } = useMutedIds()
  const signedIn = Boolean(session && profile)
  // Guests hold ANONYMOUS sessions (which do get profiles rows), so
  // session+profile alone can't tell them apart from real accounts.
  // The vote RPC rejects anonymous users ("Sign up to vote"), so the
  // UI must match: reading stays open, the vote button asks to sign in.
  const isAnon = Boolean((session?.user as { is_anonymous?: boolean } | undefined)?.is_anonymous)
  const canVote = signedIn && !isAnon
  const [event, setEvent] = useState<WeeklyEventRow | null>(null)
  const [subs, setSubs]   = useState<WeeklySubmissionRow[]>([])
  const [authors, setAuthors] = useState<Record<string, AuthorInfo>>({})
  const [myVotes, setMyVotes] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const evRes = await supabase
          .from('weekly_events').select('*').eq('id', eventId).single()
        if (evRes.error) throw evRes.error
        if (cancelled) return
        setEvent(evRes.data as WeeklyEventRow)

        const gallery = await fetchGallery(eventId)
        // Only show finalised submissions that the author actually
        // wrote something for. Pre-fix, anonymous (guest) sessions
        // could `ensureMyWeeklySubmission` just by opening the editor,
        // creating empty rows that then cluttered the vote view as
        // "Player / (no description yet)". The migration now blocks
        // guests at the RPC level; the filter here cleans up legacy
        // empty rows so the list reads as real submissions only.
        const hasContent = (s: WeeklySubmissionRow) =>
          (s.title?.trim().length ?? 0) > 0 ||
          (s.course1_text?.trim().length ?? 0) > 0
        const visible = gallery.filter(s => s.is_final && hasContent(s))
        if (cancelled) return
        setSubs(visible)

        const authorMap = await fetchSubmissionAuthors(visible.map(s => s.profile_id))
        if (cancelled) return
        setAuthors(authorMap)

        if (signedIn && profile) {
          const voted = await fetchMyVotedSubmissionIds(visible.map(s => s.id), profile.id)
          if (!cancelled) setMyVotes(voted)
        }
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [eventId, signedIn, profile?.id])

  async function onToggle(sub: WeeklySubmissionRow) {
    if (!profile || !canVote) return
    if (sub.profile_id === profile.id) return // can't self-vote
    const already = myVotes.has(sub.id)
    // Hard cap: 2 votes per voter per week. Removing a vote is always
    // allowed; adding one when you're already at 2 is blocked here so
    // the user gets immediate feedback (server enforces too).
    if (!already && myVotes.size >= MAX_VOTES_PER_VOTER) {
      setError(t('You can vote on at most {n} stories per week.', { n: MAX_VOTES_PER_VOTER }))
      return
    }
    // Optimistic update.
    setMyVotes(prev => {
      const next = new Set(prev)
      if (already) next.delete(sub.id); else next.add(sub.id)
      return next
    })
    setSubs(prev => prev.map(s =>
      s.id === sub.id
        ? { ...s, total_votes: Math.max(0, s.total_votes + (already ? -1 : 1)) }
        : s
    ))
    try {
      const newTotal = await toggleWeeklyVote(sub.id)
      setSubs(prev => prev.map(s =>
        s.id === sub.id ? { ...s, total_votes: newTotal } : s
      ))
    } catch (e) {
      // Roll back on failure.
      setMyVotes(prev => {
        const next = new Set(prev)
        if (already) next.add(sub.id); else next.delete(sub.id)
        return next
      })
      setSubs(prev => prev.map(s =>
        s.id === sub.id
          ? { ...s, total_votes: Math.max(0, s.total_votes + (already ? 1 : -1)) }
          : s
      ))
      setError(readError(e))
    }
  }

  const sorted = useMemo(
    () => [...subs].sort((a, b) => b.total_votes - a.total_votes),
    [subs],
  )

  const myVoteCount = sorted.filter(s => myVotes.has(s.id)).length
  const voteableCount = sorted.filter(s => s.profile_id !== profile?.id).length
  const atCap = myVoteCount >= MAX_VOTES_PER_VOTER

  return (
    <div className="screen">
      <BackgroundTitle text="Vote" color="#B58615" />
      <PageHeader
        title={t('Vote')}
        subtitle={event ? weekLabel(event.week_start) : '…'}
        onBack={onBack}
      />

      {/* Progress — capped to MAX_VOTES_PER_VOTER, not voteableCount,
          since each player only ever gets that many votes. */}
      {voteableCount > 0 && (
        <div className="px-5 py-3 border-b border-brand-border relative z-10 bg-brand-bg">
          <div className="flex items-center justify-between text-xs text-brand-muted mb-1.5">
            <span>{t('Your votes')}</span>
            <span>{myVoteCount}/{MAX_VOTES_PER_VOTER}</span>
          </div>
          <div className="h-1.5 rounded-full bg-brand-elevated overflow-hidden">
            <div
              className="h-full bg-game-iet-dark rounded-full transition-all duration-500"
              style={{ width: `${(myVoteCount / MAX_VOTES_PER_VOTER) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="px-5 py-4 space-y-3 overflow-y-auto pb-10 relative z-10">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {loading && (
          <>
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
          </>
        )}

        {!loading && sorted.length === 0 && (
          <div className="card p-5 text-center">
            <p className="font-semibold text-brand-text">{t('Nothing to vote on yet')}</p>
            <p className="text-sm text-brand-muted mt-1">
              {t("Stories from this week will appear here once they're finalised.")}
            </p>
          </div>
        )}

        {sorted.map(sub => {
          const author = authors[sub.profile_id]
          const isOwn = sub.profile_id === profile?.id
          const muted = mutedIds.has(sub.profile_id)
          const titleText = muted ? MUTED_PLACEHOLDER : dishDisplayName(sub, author)
          const storyText = muted ? MUTED_PLACEHOLDER : sub.course1_text
          const voted = myVotes.has(sub.id)
          const isExpanded = expanded === sub.id
          return (
            <div key={sub.id}
                 className={`iet-gold border-2 border-game-iet-dark rounded-2xl shadow-card
                             overflow-hidden transition-all
                             ${voted ? 'ring-2 ring-iet-green/50' : ''}`}>
              <button
                onClick={() => setExpanded(isExpanded ? null : sub.id)}
                className="w-full p-4 flex items-start gap-3 text-left"
              >
                <Avatar
                  name={author?.display_name ?? 'Player'}
                  avatarUrl={author?.avatar_url}
                  size="md"
                  className="shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-brand-text text-sm truncate">
                      {titleText}
                    </span>
                    {isOwn && (
                      <span className="text-[10px] bg-game-iet-light text-game-iet-dark
                                       px-2 py-0.5 rounded-full font-medium">
                        {t('You')}
                      </span>
                    )}
                    {voted && (
                      <span className="text-xs text-iet-green font-semibold">{t('✓ Voted')}</span>
                    )}
                  </div>
                  {sub.title?.trim() && (
                    <p className="text-[11px] text-brand-muted truncate">
                      by{' '}
                      {onOpenProfile && !isOwn ? (
                        // span, not button: the whole card is already a
                        // button, and HTML forbids nesting them (React 19
                        // raises it as a hydration error).
                        <span
                          role="link"
                          onClick={(e) => { e.stopPropagation(); onOpenProfile(sub.profile_id) }}
                          className="underline underline-offset-2 hover:text-brand-text
                                     active:text-brand-text cursor-pointer"
                        >
                          {author?.display_name ?? 'Player'}
                        </span>
                      ) : (
                        author?.display_name ?? 'Player'
                      )}
                    </p>
                  )}
                  <p className="text-xs text-brand-muted line-clamp-2 mt-0.5">
                    {muted ? MUTED_PLACEHOLDER : (sub.course1_text || t('(no description yet)'))}
                  </p>
                </div>
                <div className="shrink-0 flex flex-col items-center gap-0.5">
                  <span className="font-display font-bold text-game-iet-dark text-lg">
                    {sub.total_votes}
                  </span>
                  <span className="text-[10px] text-brand-muted">{t('votes')}</span>
                </div>
              </button>

              {/* Expanded full description */}
              {isExpanded && sub.course1_text.trim() && (
                <div className="border-t border-game-iet-dark/40 px-4 py-3 bg-white/40">
                  <p className="text-sm text-brand-text leading-relaxed whitespace-pre-wrap">
                    "{storyText}"
                  </p>
                  {!isOwn && (
                    <div className="flex justify-end mt-2">
                      <ReportButton submissionId={sub.id} />
                    </div>
                  )}
                </div>
              )}

              {/* Vote button — accounts only; the RPC blocks guests, so the
                  UI offers sign-in instead of a button that errors. */}
              {!isOwn && canVote && (
                <div className="px-4 pb-4 pt-1">
                  <button
                    onClick={() => onToggle(sub)}
                    disabled={!voted && atCap}
                    className={`w-full py-3 rounded-2xl font-semibold text-sm
                                flex items-center justify-center gap-2
                                active:scale-95 transition-all border-2
                                disabled:opacity-40 disabled:cursor-not-allowed
                                ${voted
                                  ? 'bg-iet-green/10 text-iet-green border-iet-green/40 shadow-xs'
                                  : 'bg-white/85 border-game-iet-dark text-brand-text shadow-card'}`}
                  >
                    {voted
                      ? t("✅ You'd eat that · {n} total", { n: sub.total_votes })
                      : atCap
                        ? t('{n}-vote limit reached', { n: MAX_VOTES_PER_VOTER })
                        : t("👍 I'd eat that!")}
                  </button>
                </div>
              )}

              {!isOwn && !canVote && (
                <div className="px-4 pb-4 pt-1">
                  <button
                    onClick={exitGuestMode}
                    className="w-full py-3 rounded-2xl font-semibold text-sm
                               bg-white/85 border-2 border-brand-border text-brand-muted
                               active:scale-95 transition-all shadow-card"
                  >
                    {t('Sign in to vote')}
                  </button>
                </div>
              )}

              {isOwn && (
                <div className="px-4 pb-4 pt-1">
                  <div className="w-full py-3 rounded-2xl bg-brand-elevated text-brand-muted
                                  font-medium text-sm text-center">
                    {t("Your entry. You can't vote for yourself.")}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
